<script lang="ts">
import { computed, defineComponent, mergeProps, nextTick, type PropType, ref, unref } from 'vue';

import { deepEqual, keyNames, noop } from '@components/utils';

import {
  makeDisabledProps,
  makeFieldProps,
  makeFocusProps,
  makeIconEvents,
  makeIconProps,
  makeInputProps,
  makeItemsProps,
  makeLazyProps,
  makeLoadingProps,
  makeLocaleProps,
  makeMenuProps,
  makeReadonlyProps,
  makeSelectProps,
  makeSizeProps,
  makeThemeProps,
  makeTransitionProps,
  makeValidationProps,
  useField,
  useFilteredSelectProps,
  useFocus,
  useForm,
  useIcon,
  useInput,
  useItems,
  useLocale,
  useMenu,
  useSelect
} from '../../composable';
import { CdsCheckbox } from '../CdsCheckbox';
import { CdsList, CdsListItem } from '../CdsList';
import { CdsMenu } from '../CdsMenu';
import { CdsTag } from '../CdsTag';
import { CdsTextInput } from '../CdsTextInput';
import { CdsIcon } from '../CdsIcon';
import { CdsDivider } from '../CdsDivider';

import type { IInternalItem } from '../../composable';

export const componentName = 'CdsDropdown';
export default defineComponent({
  name: componentName,
  components: { CdsTextInput, CdsMenu, CdsList, CdsListItem, CdsCheckbox, CdsTag, CdsIcon, CdsDivider },
  props: {
    ...makeSelectProps(),
    ...makeFieldProps(),
    ...makeInputProps(),
    ...makeValidationProps(),
    ...makeIconProps(),
    ...makeIconEvents(),
    ...makeLazyProps(),
    ...makeLocaleProps(),
    ...makeMenuProps(),
    ...makeFocusProps(),
    ...makeDisabledProps(),
    ...makeReadonlyProps(),
    ...makeLoadingProps(),
    ...makeSizeProps(),
    ...makeItemsProps(),
    ...makeTransitionProps(),
    ...makeThemeProps(),

    /**
     * Нативный HTML-select выпадающий список
     */
    nativeSelect: {
      type: Boolean as PropType<boolean>,
      default: false
    }
  },
  emits: {
    /**
     * Событие при фокусе
     * @type { boolean }
     */
    'update:focused': (value: boolean) => true,
    /**
     * Событие при изменении значения (v-model)
     * @type { any }
     */
    'update:modelValue': (value: any) => true,
    /**
     * Событие открытия меню
     * @type { boolean }
     */
    'update:menu': (value: boolean) => true,
    /**
     * Событие выбора всех элементов
     * @type { boolean }
     */
    'update:selectedAll': (value: boolean) => true
  },
  setup(props, { slots, emit, expose }) {
    const { dropdown: dropdownLocale } = useLocale(props);
    const { hasLabel, hasDescription } = useInput(props, slots, componentName);
    const { hasPrependInnerIcon, hasAppendInnerIcon } = useField(props, slots, componentName);
    const { hasAppend, hasPrepend } = useIcon(props, slots, componentName);
    const { isFocused, focus } = useFocus(props, componentName);
    const { items, transformIn, transformOut } = useItems(props);
    const { internalMenu, menuRef } = useMenu<typeof CdsMenu>(props);
    const { model, selections, selected, selectedAll, selectedIndeterminate, selectStrategy, displayItems } = useSelect(
      props,
      items,
      transformIn,
      transformOut
    );
    const form = useForm();

    const inputModelValue = computed(() =>
      unref(model)
        .map((item) => item.props.value)
        .join(', ')
    );
    const hasNoDataItem = computed(() => !props.hideNoData && !unref(displayItems).length);
    const isDirty = computed(() => unref(model).length > 0);
    const externalValidationValue = computed(() => model.externalValue);

    const dropdownClasses = computed(() => ({
      'cds-dropdown--active-menu': unref(internalMenu),
      'cds-dropdown--selected': unref(model).length,
      'cds-dropdown--ellipsis': props.ellipsis,
      'cds-dropdown--disabled': props.disabled,
      [`cds-dropdown--${props.multiple ? 'multiple' : 'single'}`]: true
    }));

    const { inputProps } = useFilteredSelectProps(props, CdsTextInput.props, [
      'modelValue',
      'validationValue',
      'readonly',
      'focused',
      'appendInnerIcon',
      'counter'
    ]);

    const inputRef = ref<typeof CdsTextInput>();
    const listRef = ref<typeof CdsList>();

    const hasList = computed(
      () => !!(!props.hideNoData || unref(displayItems).length || slots.prepend || slots.append || slots['no-data'])
    );

    function select(item: IInternalItem) {
      if (props.readonly || props.disabled) return;
      if (item.props.readonly || item.props.disabled) return;

      if (props.multiple) {
        const index = unref(selected).findIndex((selection) => deepEqual(selection, item.value));

        if (index === -1) {
          model.value = [...unref(model), item];
        } else {
          const value = [...unref(model)];
          value.splice(index, 1);
          model.value = value;
        }
      } else {
        model.value = [item];

        nextTick(() => {
          internalMenu.value = false;
        });
      }
    }

    function selectAll() {
      if (props.readonly || props.disabled) return;

      if (props.multiple) {
        if (!unref(selectedAll) || unref(selectedIndeterminate)) {
          model.value = [...unref(items)];
        } else {
          model.value = [];
        }

        emit('update:selectedAll', unref(selectedAll));
      }
    }

    let keyboardLookupPrefix = '';
    let keyboardLookupLastTime: number;
    function onKeydown(event: KeyboardEvent) {
      if (props.readonly || unref(form?.isReadonly)) return;

      if (
        [keyNames.enter, keyNames.space, keyNames.down, keyNames.up, keyNames.home, keyNames.end].includes(event.key)
      ) {
        event.preventDefault();
      }

      if ([keyNames.enter, keyNames.down, keyNames.space].includes(event.key)) {
        internalMenu.value = true;
      }

      if ([keyNames.esc, keyNames.tab].includes(event.key)) {
        internalMenu.value = false;
      }

      if (event.key === keyNames.down) {
        unref(listRef)?.focus('next');
      } else if (event.key === keyNames.up) {
        unref(listRef)?.focus('prev');
      } else if (event.key === keyNames.home) {
        unref(listRef)?.focus('first');
      } else if (event.key === keyNames.end) {
        unref(listRef)?.focus('last');
      }

      const KEYBOARD_LOOKUP_THRESHOLD = 1000; // milliseconds

      function checkPrintable(event: KeyboardEvent) {
        const isPrintableChar = event.key.length === 1;
        const noModifier = !event.ctrlKey && !event.metaKey && !event.altKey;
        return isPrintableChar && noModifier;
      }

      if (props.multiple || !checkPrintable(event)) return;

      const now = performance.now();
      if (now - keyboardLookupLastTime > KEYBOARD_LOOKUP_THRESHOLD) {
        keyboardLookupPrefix = '';
      }
      keyboardLookupPrefix += event.key.toLowerCase();
      keyboardLookupLastTime = now;

      const item = unref(items).find((item) => item.title.toLowerCase().startsWith(keyboardLookupPrefix));
      if (item !== undefined) {
        model.value = [item];
      }
    }

    function onClear() {
      if (props.openOnClear) {
        internalMenu.value = true;
      }

      model.value = [];
    }

    function onBlur(event: FocusEvent) {
      if (!unref(listRef)?.$el.contains(event.relatedTarget as HTMLElement)) {
        internalMenu.value = false;
      }
    }

    function onFocusin() {
      focus();
    }

    function onFocusout(event: FocusEvent) {
      if (event.relatedTarget == null) {
        unref(inputRef)?.focus();
      }
    }

    function onInputModelValue(value: string) {
      if (value == null) {
        model.value = [];
      }
    }

    function onInputNativeSelect(event: Event) {
      const value = (event.target as HTMLSelectElement).value;
      const item = unref(items).find((item) => item.props.value === value);
      if (item) select(item);
    }

    function onOpenNativeSelect() {
      if (props.nativeSelect) {
        const select = unref(inputRef)?.fieldRef.querySelector('select');
        select.showPicker();
      }
    }

    function onMousedownControl() {
      if ((props.hideNoData && !unref(items).length) || props.readonly || unref(form?.isReadonly)) return;
      if (props.nativeSelect) onOpenNativeSelect();

      internalMenu.value = !internalMenu.value;
    }

    function onMousedownMenuIcon(event: MouseEvent) {
      if (props.readonly || props.disabled) return;
      if (props.nativeSelect) onOpenNativeSelect();

      event.preventDefault();
      event.stopPropagation();

      internalMenu.value = !unref(internalMenu);
    }

    function mergeItemProps(item: IInternalItem) {
      return mergeProps(item.props, { onClick: () => select(item) });
    }

    expose({
      isFocused,
      menu: internalMenu,
      focus,
      select,
      selectAll,
      menuRef,
      listRef,
      textInputRef: inputRef
    });

    return {
      model,
      internalMenu,
      inputModelValue,
      displayItems,
      selections,
      selected,
      selectedAll,
      selectedIndeterminate,
      selectStrategy,
      isDirty,
      isFocused,
      externalValidationValue,
      inputRef,
      menuRef,
      listRef,
      inputProps,
      hasList,
      hasLabel,
      hasDescription,
      hasPrepend,
      hasAppend,
      hasPrependInnerIcon,
      hasAppendInnerIcon,
      hasNoDataItem,
      dropdownClasses,
      dropdownLocale,
      noop,
      select,
      selectAll,
      mergeItemProps,
      onKeydown,
      onFocusin,
      onFocusout,
      onBlur,
      onClear,
      onInputModelValue,
      onMousedownControl,
      onMousedownMenuIcon,
      onInputNativeSelect
    };
  }
});
</script>

<template>
  <cds-text-input
    ref="inputRef"
    v-model:focused="isFocused"
    :model-value="inputModelValue"
    :placeholder="placeholder"
    :dirty="isDirty"
    :validation-value="externalValidationValue"
    readonly
    :counter="false"
    :class="dropdownClasses"
    class="cds-dropdown"
    v-bind="inputProps"
    @blur="onBlur"
    @keydown="onKeydown"
    @click:clear="onClear"
    @update:model-value="onInputModelValue"
    @mousedown:control="onMousedownControl"
  >
    <!--
      @slot Выпадающее меню
      @type CdsMenu
    -->
    <slot>
      <select v-if="nativeSelect" :id="id" :disabled="disabled" @input="onInputNativeSelect">
        <option value="" disabled hidden selected>{{ placeholder }}</option>
        <template v-for="(item, idx) in displayItems" :key="`${item.value}-${idx}`">
          <option
            v-if="!item.children"
            :value="item.value"
            :label="item.title"
            :selected="item.value === model"
            :disabled="item.props.disabled"
          />
          <optgroup v-else :label="item.title">
            <option
              v-for="(childItem, childIdx) in item.children"
              :key="`${childItem.value}-${childIdx}`"
              :value="childItem.value"
              :label="childItem.title"
              :disabled="childItem.props.disabled"
              :selected="childItem.value === model"
            />
          </optgroup>
        </template>
      </select>

      <cds-menu
        v-else
        ref="menuRef"
        v-model="internalMenu"
        :eager="eager"
        :max-height="menuHeight"
        :close-on-content-click="false"
        :open-on-click="false"
        :transition="transition"
        activator="parent"
        content-classes="cds-dropdown__content"
        v-bind="menuProps"
      >
        <cds-list
          v-if="hasList"
          ref="listRef"
          :selected="selected"
          :select-strategy="selectStrategy"
          v-bind="listProps"
          @focusin="onFocusin"
          @focusout="onFocusout"
        >
          <!--
            @slot Нет данных
            @type string | html
          -->
          <slot v-if="hasNoDataItem" name="no-data">
            <cds-list-item :title="dropdownLocale.noData" />
          </slot>
          <!--
            @slot Элемент перед списком
            @type string | html
          -->
          <slot name="prepend-item">
            <cds-list-item
              v-if="multiple && isSelectAll"
              is-clickable
              :title="dropdownLocale.selectAll"
              class="cds-dropdown__select-all"
              @click="selectAll"
            >
              <template #prepend>
                <cds-checkbox
                  v-model="selectedAll"
                  :indeterminate="selectedIndeterminate"
                  tabindex="-1"
                  hide-messages
                />
              </template>
            </cds-list-item>
            <cds-divider v-if="multiple && isSelectAll" />
          </slot>
          <template v-for="(item, index) in displayItems" :key="index">
            <!--
              @slot Элемент списка
              @type CdsListItem
            -->
            <slot name="item" v-bind="{ item, index, props: mergeItemProps(item) }">
              <cds-list-item :disabled="readonly" v-bind="item.props" @click="select(item)">
                <template v-if="multiple && !hideSelected" #prepend="{ isSelected }">
                  <cds-checkbox :model-value="isSelected" tabindex="-1" hide-messages />
                </template>
              </cds-list-item>
            </slot>
          </template>
          <!--
            @slot Элемент после списка
            @type string | html
          -->
          <slot name="append-item" />
        </cds-list>
      </cds-menu>

      <div v-for="(item, index) in selections" :key="item.value" class="cds-dropdown__selection">
        <!--
            @slot Выбранные элементы в поле
            @type string | html
          -->
        <slot name="selection" v-bind="{ item, index }">
          <span class="cds-dropdown__selection--text">
            {{ item.title }}
          </span>
          <span v-if="multiple && index < selections.length - 1" class="cds-dropdown__selection--comma">,</span>
        </slot>
      </div>
    </slot>

    <template #append-inner>
      <!--
        @slot Элемент внутри после значением поля
        @type string | html
      -->
      <slot name="append-inner" />
      <cds-icon :name="menuIcon" class="cds-dropdown__menu-icon" @click="noop" @mousedown="onMousedownMenuIcon" />
    </template>

    <template v-if="hasLabel" #label>
      <!--
        @slot Лейбл
        @type string | html
      -->
      <slot name="label" />
    </template>
    <template v-if="hasDescription" #description>
      <!--
        @slot Описание
        @type string | html
      -->
      <slot name="description" />
    </template>
    <template v-if="hasPrepend" #prepend>
      <!--
        @slot Элемент перед значением поля
        @type string | html
      -->
      <slot name="prepend" />
    </template>
    <template v-if="hasPrependInnerIcon || (counter && multiple)" #prepend-inner>
      <cds-tag v-if="selected.length > 0" :text="`${selected.length}`" />
      <!--
        @slot Элемент внутри перед значением поля
        @type string | html
      -->
      <slot name="prepend-inner" />
    </template>
    <template v-if="hasAppend" #append>
      <!--
        @slot Элемент после значения поля
        @type string | html
      -->
      <slot name="append" />
    </template>
    <template #messages>
      <!--
        @slot Сообщения и подсказки
        @type string | html
      -->
      <slot name="messages" />
    </template>
  </cds-text-input>
</template>

<style lang="scss">
@import './CdsDropdown';
</style>
